import { selectEnv } from "@app/config";
import { createSelector } from "@app/fx";
import { defaultEntity, extractIdFromLink } from "@app/hal";
import { schema } from "@app/schema";
import { parseOrgIds, titleCase } from "@app/string-utils";
import {
  type Config,
  type LinkResponse,
  type Organization,
  excludesFalse,
} from "@app/types";

export interface OrganizationResponse {
  address: string | null;
  city: string | null;
  created_at: string;
  emergency_phone: string | null;
  id: string;
  name: string;
  ops_alert_email: string | null;
  ops_alert_owner_notify: boolean;
  primary_phone: string | null;
  security_alert_email: string | null;
  security_alert_owner_notify: boolean;
  state: string | null;
  updated_at: string;
  zip: string | null;
  reauth_required?: boolean;
  sso_enforced: boolean;
  _links: {
    billing_detail: LinkResponse;
    invitations: LinkResponse;
    roles: LinkResponse;
    security_officer: LinkResponse;
    self: LinkResponse;
    users: LinkResponse;
  };
  _type: "organization";
}

export const defaultOrgResponse = (
  o: Partial<OrganizationResponse> = {},
): OrganizationResponse => {
  const now = new Date().toISOString();
  return {
    address: "",
    city: "",
    created_at: now,
    updated_at: now,
    emergency_phone: "",
    id: "",
    name: "",
    ops_alert_email: "",
    ops_alert_owner_notify: true,
    primary_phone: "",
    security_alert_email: "",
    security_alert_owner_notify: true,
    state: "",
    zip: "",
    sso_enforced: false,
    _links: {
      billing_detail: { href: "" },
      invitations: { href: "" },
      roles: { href: "" },
      security_officer: { href: "" },
      self: { href: "" },
      users: { href: "" },
      ...o._links,
    },
    _type: "organization",
    ...o,
  };
};

export const selectOrganizationById = schema.organizations.selectById;
export const selectOrganizationsAsList = createSelector(
  schema.organizations.selectTable,
  (orgMap) => {
    return Object.values(orgMap)
      .filter(excludesFalse)
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
  },
);
export const selectOrganizationSelectedId = schema.organizationSelected.select;
export const hasOrganization = (o: Organization): boolean => !!o.id;
export const selectHasManyOrgs = createSelector(
  selectOrganizationsAsList,
  (organizations) => organizations.length > 1,
);

export const selectOrganizationSelected = createSelector(
  selectOrganizationsAsList,
  selectOrganizationSelectedId,
  (orgs, id): Organization => {
    if (orgs.length === 0) {
      return schema.organizations.empty;
    }
    if (!id) {
      return schema.organizations.empty;
    }

    const org = orgs.find((o) => o.id === id);
    if (org) {
      return org;
    }

    return schema.organizations.empty;
  },
);

type OrgIdsFlag = Extract<keyof Config, `${string}OrgIds`>;

interface FeatureFlagDef {
  key: OrgIdsFlag;
  // Beta Features and Account Integrations are open to everyone in local
  // dev; the other flags are not — they're exercised by route guards whose
  // tests expect the allowlist to apply even when config.isDev is true.
  bypassInDev?: boolean;
}

const FF_DEF_BETA_FEATURE: FeatureFlagDef = {
  key: "betaFeatureOrgIds",
  bypassInDev: true,
};
const FF_DEF_EXTERNAL_AWS: FeatureFlagDef = {
  key: "externalAwsFeatureOrgIds",
  bypassInDev: true,
};
const FF_DEF_LABS: FeatureFlagDef = { key: "labsFlagDetectionOrgIds" };
const FF_DEF_SCHEDULED_HAS: FeatureFlagDef = { key: "scheduledHasOrgIds" };

// Add a flag here to wire up its standard selector and show it in the
// impersonate page's feature flags table.
const FEATURE_FLAG_DEFS: FeatureFlagDef[] = [
  FF_DEF_BETA_FEATURE,
  FF_DEF_EXTERNAL_AWS,
  FF_DEF_LABS,
  FF_DEF_SCHEDULED_HAS,
];

function isFeatureFlagEnabled(
  orgId: string,
  config: Config,
  def: FeatureFlagDef,
): boolean {
  if (def.bypassInDev && config.isDev) {
    return true;
  }

  return parseOrgIds(config[def.key]).includes(orgId);
}

const createStandardFeatureFlagSelector = (def: FeatureFlagDef) =>
  createSelector(selectOrganizationSelectedId, selectEnv, (orgId, config) =>
    isFeatureFlagEnabled(orgId, config, def),
  );

// If a flag needs custom logic, write its own selector instead of using the
// standard one above.
export const selectHasBetaFeatures =
  createStandardFeatureFlagSelector(FF_DEF_BETA_FEATURE);
export const selectHasExternalAws =
  createStandardFeatureFlagSelector(FF_DEF_EXTERNAL_AWS);
export const selectHasLabsFlagDetection =
  createStandardFeatureFlagSelector(FF_DEF_LABS);
export const selectHasScheduledHas =
  createStandardFeatureFlagSelector(FF_DEF_SCHEDULED_HAS);

export interface FeatureFlagRow {
  key: string;
  label: string;
  enabled: boolean;
  orgIds: string[];
}

// One row per entry in FEATURE_FLAG_DEFS — add a new def there to show it
// here too.
export const selectAllFeatureFlags = createSelector(
  selectOrganizationSelectedId,
  selectEnv,
  (orgId, config): FeatureFlagRow[] =>
    FEATURE_FLAG_DEFS.map((def) => {
      const orgIds = parseOrgIds(config[def.key]);
      return {
        key: def.key,
        label: titleCase(def.key.replace(/OrgIds$/, "")),
        enabled: isFeatureFlagEnabled(orgId, config, def),
        orgIds,
      };
    }),
);

export const selectHasMaintenanceWindow = createSelector(
  selectOrganizationSelectedId,
  selectEnv,
  (orgId, config) => {
    if (!config.maintenanceWindowOrgIds) {
      return false;
    }

    const ids = config.maintenanceWindowOrgIds
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    return ids.includes(orgId);
  },
);

function deserializeOrganization(o: OrganizationResponse): Organization {
  return {
    id: o.id,
    name: o.name,
    updatedAt: o.updated_at,
    ssoEnforced: o.sso_enforced,
    billingDetailId: extractIdFromLink(o._links.billing_detail),
    reauthRequired: o.reauth_required || false,
    address: o.address || "",
    city: o.city || "",
    zip: o.zip || "",
    state: o.state || "",
    securityAlertEmail: o.security_alert_email || "",
    securityAlertOwnerNotify: o.security_alert_owner_notify ?? true,
    opsAlertEmail: o.ops_alert_email || "",
    opsAlertOwnerNotify: o.ops_alert_owner_notify ?? true,
    emergencyPhone: o.emergency_phone || "",
    primaryPhone: o.primary_phone || "",
  };
}

export const entities = {
  organization: defaultEntity({
    id: "organization",
    save: schema.organizations.add,
    deserialize: deserializeOrganization,
  }),
};
